/* eslint-disable no-undef */
import { showErrorAlert } from 'components/alf-design';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from 'react-router-dom';

import config from '../config';
import { isH3TXPolicy } from './policyType';

const usePayment = ({
  submitPayment,
  getPolicy,
  policyNumber,
  policySystem,
  oneTimePayment,
}) => {
  const navigate = useNavigate();
  const [dataResult, setDataResult] = useState(null);
  const [error, setError] = useState(null);
  const initEventHandlers = (container, toggleLoading, insured) => {
    container.off();

    container.on('portalOne.load', function () {
      toggleLoading();
    });

    container.on('portalOne.paymentComplete', function (e, data) {
      datadogRum.addAction('payment.submission.succeeded');

      const method = data.paymentCategory === 'CreditCard' ? 'card' : 'bank';
      const successData = {
        method,
        data: {
          paymentInformation: {
            transactionId: data.transactionId,
            tokenId: data.tokenId,
            authCode: method === 'card' ? data.authCode : '',
            transactionDate: moment(data.transactionDate).format('YYYY-MM-DD'),
            cardType: method === 'card' ? data.cardType : '',
            lastFourDigits: method === 'card' ? data.lastFourDigits : '',
          },
          orderInformation: {
            amountDetails: {
              totalAmount: `${data.paymentAmount}`,
              currency: 'USD',
            },
            billTo: {
              firstName: insured?.firstName,
              lastName: insured?.lastName,
              address1: insured?.street,
              city: insured?.city,
              state: insured?.state,
              zipCode: insured?.zip,
            },
          },
        },
      };
      setDataResult(successData);
    });

    container.on('portalOne.error', function (e, data) {
      datadogRum.addAction('payment.submission.failed');
      setError(data);
    });

    container.on('portalOne.unload', function () {
      const event = new CustomEvent('OneIncModalClosed');
      window.dispatchEvent(event);
    });

    const modalContainer = document.getElementById('portalOneContainer');
    if (modalContainer) {
      const observer = new MutationObserver(() => {
        const isHidden = !window.$(modalContainer).is(':visible');
        if (isHidden) {
          const event = new CustomEvent('OneIncModalClosed');
          window.dispatchEvent(event);
          observer.disconnect();
        }
      });
      observer.observe(modalContainer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  };

  const getCustomerId = (customerName, partyId, paymentData) => {
    return new Promise((resolve) => {
      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-Type': 'application/json',
        },
        body: JSON.stringify({
          ExternalCustomerId: partyId,
          CustomerName: customerName,
          policySystem: paymentData.policySystem,
          company: paymentData.company,
          isMGA: paymentData.isFusion || isH3TXPolicy(paymentData.policyNumber),
        }),
      };

      fetch('/api/oneinc/customer', options)
        .then((response) => response.json())
        .then((response) => resolve(response.CustomerId))
        .catch((err) => {
          datadogLogs.logger.error(`Create OneInc Account - ${policyNumber}`, {
            err,
          });
          showErrorAlert({
            message: err,
            description:
              'Something went wrong submitting your payment. Please check your connection and try again.',
          });
        });
    });
  };

  useEffect(() => {
    if (!dataResult) return;

    const handleModalClose = async () => {
      if (config.oneIncWebhook === '0' && policySystem === 'Violet') {
        try {
          await submitPayment(dataResult);

          if (getPolicy && policyNumber) {
            getPolicy(
              policyNumber,
              null,
              (err) => {
                datadogLogs.logger.error(`Get Policy - ${policyNumber}`, {
                  err,
                });
              },
              { force: true },
            );
          }
        } catch (e) {
          datadogLogs.logger.error('Submit payment failed', { e });
        }
      }
      if (config.oneIncWebhook === '1') {
        if (getPolicy && policyNumber) {
          getPolicy(
            policyNumber,
            null,
            (err) => {
              datadogLogs.logger.error(`Get Policy - ${policyNumber}`, {
                err,
              });
            },
            { force: true },
          );
        }
      }

      if (oneTimePayment) {
        navigate('/');
      }
    };

    window.addEventListener('OneIncModalClosed', handleModalClose);

    return () => {
      window.removeEventListener('OneIncModalClosed', handleModalClose);
    };
  }, [dataResult]);

  useEffect(() => {
    if (error !== null) {
      datadogLogs.logger.error(`OneInc Error`, { error });
    }
  }, [error]);

  const handleOneIncPayment = (data, toggleLoading) => {
    const payorPartyId =
      data.policySystem === 'IPX' ? policyNumber : data?.billing?.payorPartyId;
    const accountGroupCode =
      data.policySystem === 'IPX'
        ? 'Slide IPX Premium'
        : data.isFusion || isH3TXPolicy(data.policyNumber)
          ? 'Slide MGA'
          : data.company === 'Slide Specialty Insurance Company'
            ? 'Slide Specialty Insurance Company'
            : 'Slide Insurance Premium';

    getCustomerId(
      `${data?.insured?.firstName} ${data?.insured?.lastName}`,
      payorPartyId,
      data,
    ).then((custId) => {
      fetch('/api/oneinc/session', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: custId,
          policySystem: data.policySystem,
          company: data.company,
          isMGA: data.isFusion || isH3TXPolicy(data.policyNumber),
        }),
      })
        .then((response) => response.json())
        .then((result) => {
          let container = window.$('#portalOneContainer');
          container.portalOne();
          initEventHandlers(container, toggleLoading, data?.insured);
          let dialog = container.data('portalOne');
          dialog.makePayment({
            paymentCategory: data.paymentCategory ?? 'UserSelect',
            feeContext: 'PaymentWithFee',
            amountContext: 'AmountDueOnly',
            minAmountDue: data?.minAmountDue,
            accountBalance: data?.billing?.outstandingBalance,
            billingZip: data?.insured?.zip,
            billingAddressStreet: `${data?.insured?.street}, ${data?.insured?.city}, ${data?.insured?.state}`,
            policyHolderName: `${data?.insured?.firstName} ${data?.insured?.lastName}`,
            clientReferenceData1: data?.policyNumber,
            clientReferenceData2: `COP_${data?.policyNumber}`,
            clientReferenceData3: data?.billing?.accountId,
            clientReferenceData4: config.oneIncWebhook,
            confirmationDisplay: 'true',
            saveOption: 'Save',
            acknowledgmentRequired: 'true',
            customerId: custId,
            sessionId: result.PortalOneSessionKey,
            accountGroupCode: accountGroupCode,
          });
        });
    });
  };

  return { handleOneIncPayment };
};

export default usePayment;
